import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { TrendingUp, DollarSign, ShoppingCart, Gift, Star } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/context/auth/useAuth";
import { useQuery } from "@tanstack/react-query";

interface PromoCodeSummary {
  total_campaigns: number;
  total_gross_revenue: number;
  total_net_revenue: number;
  total_sms_cost: number;
  total_orders: number;
  average_conversion_rate: number;
  top_promo_codes: {
    promo_code: string;
    gross_revenue: number;
    orders_count: number;
  }[];
}

interface PromoPerformanceSummaryCardProps {
  restaurantId?: string;
  organisationId?: string;
}

export const PromoPerformanceSummaryCard: React.FC<PromoPerformanceSummaryCardProps> = ({
  restaurantId,
  organisationId
}) => {
  const { user } = useAuth();
  const [dateRange, setDateRange] = useState<string>('30');

  const {
    data: summary,
    isLoading,
    error
  } = useQuery({
    queryKey: ['promo-performance-summary', restaurantId, organisationId, dateRange],
    queryFn: async () => {
      let query = supabase
        .from('sms_campaigns')
        .select(`
          id,
          promo_code_id,
          recipient_count,
          schedule_time,
          promo_codes!sms_campaigns_promo_code_id_fkey (
            code
          )
        `)
        .not('promo_code_id', 'is', null)
        .not('schedule_time', 'is', null);

      if (restaurantId) {
        query = query.eq('restaurant_id', restaurantId);
      } else if (organisationId) {
        query = query.eq('organisation_id', organisationId);
      }

      // Add date range filter
      if (dateRange !== 'all') {
        const daysBack = parseInt(dateRange);
        const cutoffDate = new Date();
        cutoffDate.setDate(cutoffDate.getDate() - daysBack);
        query = query.gte('schedule_time', cutoffDate.toISOString());
      }

      const { data: campaigns, error } = await query;
      if (error) throw error;

      if (!campaigns || campaigns.length === 0) {
        return {
          total_campaigns: 0,
          total_gross_revenue: 0,
          total_net_revenue: 0,
          total_sms_cost: 0,
          total_orders: 0,
          average_conversion_rate: 0,
          top_promo_codes: []
        };
      }

      // Get performance data for each campaign
      const performancePromises = campaigns.map(async (campaign) => {
        const { data, error } = await supabase.rpc('calculate_campaign_promo_performance', {
          p_campaign_id: campaign.id
        });
        if (error) throw error;
        return data?.[0] || null;
      });

      const performanceResults = await Promise.all(performancePromises);
      const validResults = performanceResults.filter(result => result !== null);

      if (validResults.length === 0) {
        return {
          total_campaigns: campaigns.length,
          total_gross_revenue: 0,
          total_net_revenue: 0,
          total_sms_cost: 0,
          total_orders: 0,
          average_conversion_rate: 0,
          top_promo_codes: []
        };
      }

      // Aggregate the results
      const totalGrossRevenue = validResults.reduce((sum, result) => sum + (result.gross_revenue || 0), 0);
      const totalNetRevenue = validResults.reduce((sum, result) => sum + (result.net_revenue || 0), 0);
      const totalSMSCost = validResults.reduce((sum, result) => sum + (result.sms_cost || 0), 0);
      const totalOrders = validResults.reduce((sum, result) => sum + (result.orders_count || 0), 0);
      const totalRecipients = campaigns.reduce((sum, campaign) => sum + (campaign.recipient_count || 0), 0);
      const averageConversionRate = totalRecipients > 0 ? (totalOrders / totalRecipients) * 100 : 0;

      // Get top performing promo codes
      const promoCodePerformance = new Map();
      validResults.forEach(result => {
        const code = result.promo_code;
        if (!promoCodePerformance.has(code)) {
          promoCodePerformance.set(code, {
            promo_code: code,
            gross_revenue: 0,
            orders_count: 0
          });
        }
        const existing = promoCodePerformance.get(code);
        existing.gross_revenue += result.gross_revenue || 0;
        existing.orders_count += result.orders_count || 0;
      });

      const topPromoCodes = Array.from(promoCodePerformance.values())
        .sort((a, b) => b.gross_revenue - a.gross_revenue)
        .slice(0, 3);

      return {
        total_campaigns: campaigns.length,
        total_gross_revenue: totalGrossRevenue,
        total_net_revenue: totalNetRevenue,
        total_sms_cost: totalSMSCost,
        total_orders: totalOrders,
        average_conversion_rate: averageConversionRate,
        top_promo_codes: topPromoCodes
      };
    },
    enabled: !!(restaurantId || organisationId),
    staleTime: 300000 // 5 minutes
  });

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-NZ', {
      style: 'currency',
      currency: 'NZD',
      minimumFractionDigits: 2
    }).format(amount);
  };

  const formatPercentage = (value: number) => {
    return `${value.toFixed(1)}%`;
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Gift className="h-5 w-5" />
            Promo Code Performance Summary
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <div className="text-muted-foreground">Loading performance summary...</div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error || !summary) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Gift className="h-5 w-5" />
            Promo Code Performance Summary
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8 text-red-600">
            Error loading summary data
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Gift className="h-5 w-5" />
            Promo Code Performance Summary
          </div>
          <Select value={dateRange} onValueChange={setDateRange}>
            <SelectTrigger className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Last 7 days</SelectItem>
              <SelectItem value="30">Last 30 days</SelectItem>
              <SelectItem value="90">Last 90 days</SelectItem>
              <SelectItem value="all">All time</SelectItem>
            </SelectContent>
          </Select>
        </CardTitle>
      </CardHeader>
      
      {summary.total_campaigns === 0 ? (
        <CardContent>
          <div className="text-center py-8 text-muted-foreground">
            No campaigns with promo codes found for the selected time period.
          </div>
        </CardContent>
      ) : (
      
      <CardContent className="space-y-6">
        {/* Key Metrics */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="text-center">
            <div className="text-2xl font-bold text-blue-600">
              {summary.total_campaigns}
            </div>
            <div className="text-xs text-muted-foreground">Total Campaigns</div>
          </div>
          
          <div className="text-center">
            <div className="text-2xl font-bold text-green-600">
              {formatCurrency(summary.total_gross_revenue)}
            </div>
            <div className="text-xs text-muted-foreground">Total Revenue</div>
          </div>
          
          <div className="text-center">
            <div className="text-2xl font-bold text-purple-600">
              {formatCurrency(summary.total_net_revenue)}
            </div>
            <div className="text-xs text-muted-foreground">Net Revenue</div>
          </div>
          
          <div className="text-center">
            <div className="text-2xl font-bold text-orange-600">
              {formatPercentage(summary.average_conversion_rate)}
            </div>
            <div className="text-xs text-muted-foreground">Avg Conversion</div>
          </div>
        </div>

        {/* Top Performing Promo Codes */}
        {summary.top_promo_codes.length > 0 && (
          <div className="space-y-3">
            <h4 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">
              Top Performing Promo Codes
            </h4>
            
            <div className="space-y-2">
              {summary.top_promo_codes.map((promo, index) => (
                <div key={promo.promo_code} className="flex items-center justify-between p-3 bg-muted rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-1">
                      <Star className="h-4 w-4 text-yellow-500" />
                      <span className="text-sm font-medium">#{index + 1}</span>
                    </div>
                    <Badge variant="outline">{promo.promo_code}</Badge>
                  </div>
                  
                  <div className="flex items-center gap-4 text-sm">
                    <div className="flex items-center gap-1">
                      <DollarSign className="h-3 w-3 text-green-600" />
                      <span className="font-medium">{formatCurrency(promo.gross_revenue)}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <ShoppingCart className="h-3 w-3 text-blue-600" />
                      <span>{promo.orders_count} orders</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Additional Metrics */}
        <div className="pt-4 border-t">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-center">
            <div>
              <div className="flex items-center justify-center gap-1 mb-1">
                <ShoppingCart className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm font-medium">Total Orders</span>
              </div>
              <div className="text-lg font-bold">{summary.total_orders.toLocaleString()}</div>
            </div>
            
            <div>
              <div className="flex items-center justify-center gap-1 mb-1">
                <DollarSign className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm font-medium">Avg Order Value</span>
              </div>
              <div className="text-lg font-bold">
                {summary.total_orders > 0 
                  ? formatCurrency(summary.total_gross_revenue / summary.total_orders)
                  : formatCurrency(0)}
              </div>
            </div>
            
            <div>
              <div className="flex items-center justify-center gap-1 mb-1">
                <TrendingUp className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm font-medium">ROI</span>
              </div>
              <div className="text-lg font-bold">
                {summary.total_sms_cost > 0 
                  ? formatPercentage((summary.total_net_revenue / summary.total_sms_cost) * 100)
                  : '0%'}
              </div>
            </div>
          </div>
        </div>
      </CardContent>
      )}
    </Card>
  );
};